/**
 * Language-aware date and number locale resolution.
 * Single source of truth for date-fns locale and Intl locale strings.
 * Used by utils (formatDate, formatCurrency, formatPrice) and all components.
 */

import type { Locale } from "date-fns";
import { fr } from "date-fns/locale";
import { enUS } from "date-fns/locale";
import { arSA } from "date-fns/locale";

const dateLocales: Record<string, Locale> = {
  fr,
  en: enUS,
  ar: arSA,
};

const numberLocales: Record<string, string> = {
  fr: "fr-FR",
  en: "en-US",
  ar: "ar-MA",
};

const WEEKDAYS_SHORT_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
/** Single-letter headers for narrow calendars (sidebar). */
const WEEKDAYS_MINIMAL_FR = ["L", "M", "M", "J", "V", "S", "D"];
const WEEKDAYS_MINIMAL_EN = ["M", "T", "W", "T", "F", "S", "S"];
const WEEKDAYS_MINIMAL_AR = ["ن", "ث", "ر", "خ", "ج", "س", "ح"];
const WEEKDAYS_SHORT_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS_SHORT_AR = ["إث", "ثل", "أر", "خم", "جم", "سب", "أح"];
const WEEKDAYS_FULL_FR = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];
const WEEKDAYS_FULL_EN = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const WEEKDAYS_FULL_AR = [
  "الإثنين",
  "الثلاثاء",
  "الأربعاء",
  "الخميس",
  "الجمعة",
  "السبت",
  "الأحد",
];

function langKey(lang: string): "fr" | "en" | "ar" {
  if (lang?.startsWith("ar")) return "ar";
  if (lang?.startsWith("fr")) return "fr";
  return "en";
}

/**
 * Returns the date-fns locale for the given language.
 * Use for all format(..., { locale }) calls.
 */
export function getDateLocale(lang: string): Locale {
  return dateLocales[langKey(lang)] ?? enUS;
}

/**
 * Returns the Intl/NumberFormat locale string for the given language.
 * Use for formatCurrency, formatPrice, toLocaleString.
 */
export function getNumberLocale(lang: string): string {
  return numberLocales[langKey(lang)] ?? "en-US";
}

/**
 * Returns short weekday names (Mon, Tue, ... or Lun, Mar, ...) for calendar headers.
 */
export function getWeekdaysShort(lang?: string): string[] {
  const key = langKey(lang ?? "fr");
  if (key === "ar") return WEEKDAYS_SHORT_AR;
  if (key === "fr") return WEEKDAYS_SHORT_FR;
  return WEEKDAYS_SHORT_EN;
}

/** Minimal weekday labels (one character) for narrow calendar columns. */
export function getWeekdaysMinimal(lang?: string): string[] {
  const key = langKey(lang ?? "fr");
  if (key === "ar") return WEEKDAYS_MINIMAL_AR;
  if (key === "fr") return WEEKDAYS_MINIMAL_FR;
  return WEEKDAYS_MINIMAL_EN;
}

/**
 * Returns full weekday names (Monday, ... or Lundi, ...) for accessibility/display.
 */
export function getWeekdaysFull(lang?: string): string[] {
  const key = langKey(lang ?? "fr");
  if (key === "ar") return WEEKDAYS_FULL_AR;
  if (key === "fr") return WEEKDAYS_FULL_FR;
  return WEEKDAYS_FULL_EN;
}
